import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { publisherResultImportsApi } from '@/api/publisher-result-imports';
import { isPublisherResultImportActive } from '@/lib/publisher-result-imports';
import type {
    PublisherResultImport,
    PublisherResultImportStatus,
} from '@/types/publisher-result-import';

const POLL_INTERVAL_MS = 2_500;

/** One page is plenty: this only ever reads the imports of a single report. */
const PAGE_SIZE = 20;

/**
 * The statuses the dock speaks for. The rest belong to somebody else:
 * `approved_pending_report` is the banner at the top of the editor, and
 * approved/cancelled/archived are simply over.
 */
const TRACKED_STATUSES = new Set<PublisherResultImportStatus>([
    'uploaded',
    'queued',
    'extracting_text',
    'deidentifying',
    'extracting_tables',
    'duplicate_warning',
    'ready_for_approval',
    'failed',
]);

type UseReportPublisherImportsInput = {
    patientId: string | null | undefined;
    reportId: string | null | undefined;
    /** Gate: the editor already knows, cheaply, whether anything is going on. */
    hasSignal: boolean;
    enabled: boolean;
};

/**
 * The imports of the current report, kept fresh while any of them is moving.
 *
 * The editor already polls `patients/approval-status` for the whole page, but
 * that payload carries counts only — no filename, no stage. This fills the gap
 * from the patient listing endpoint, and stays quiet until there is something
 * to say: with `hasSignal` false the query never runs, so a report with no
 * imports costs zero requests. Once opened, the gate stays open for the
 * session, because the interesting states (ready, failed) arrive precisely
 * when the processing count drops back to zero.
 */
export function useReportPublisherImports({
    patientId,
    reportId,
    hasSignal,
    enabled,
}: UseReportPublisherImportsInput): PublisherResultImport[] {
    const [gateOpen, setGateOpen] = useState(false);

    useEffect(() => {
        if (hasSignal) setGateOpen(true);
    }, [hasSignal]);

    const { data } = useQuery({
        queryKey: ['publisher-result-imports', 'report-active', reportId],
        queryFn: async () => {
            const response = await publisherResultImportsApi.listPatientImports(patientId || '', {
                page: 1,
                pageSize: PAGE_SIZE,
            });

            return response.data;
        },
        enabled: enabled && gateOpen && Boolean(patientId) && Boolean(reportId),
        // Stage-driven, like the modal's own poll: it stops the moment nothing
        // is moving, leaving the settled records in cache for the dock to show.
        refetchInterval: (query) =>
            query.state.data?.some(
                (record) =>
                    record.source_report_id === reportId && isPublisherResultImportActive(record),
            )
                ? POLL_INTERVAL_MS
                : false,
    });

    return useMemo(
        () =>
            (data ?? []).filter(
                (record) =>
                    record.source_report_id === reportId && TRACKED_STATUSES.has(record.status),
            ),
        [data, reportId],
    );
}
